import moment from "moment";
import React, { FC, useState } from "react";
import { LuPenLine } from "react-icons/lu";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { ContactFileModel } from "../../factories/contacts/models/contact-file.model";
import { getLinkedFirm } from "../../sessionStorage/sessionStorage";
import { useGetContactQuery } from "../../slices/ContactsSlice";
import {
  useGetContactFilesQuery,
  useGetOrgContactFilesQuery,
} from "../../slices/FilesSlice";
import {
  useCancelSignatureMutation,
  useGetClientSignaturesQuery,
  useRemindSignatureMutation,
} from "../../slices/SignaturesSlice";
import { PortalEmptyState } from "../clientPortal/PortalEmptyState";
import {
  errorText,
  modalCancel,
  modalPrimary,
  PortalModal,
} from "../clientPortal/PortalModal";
import { SignatureRequestView } from "./signature.types";
import { newestFilesFirst, signerLine, statusTone } from "./signature.utils";

const tone = {
  waiting: "bg-[#FFF4DB] text-[#8A5A00]",
  done: "bg-[#E9F9F1] text-[#147A4B]",
  stopped: "bg-[#FDECEC] text-[#B42318]",
};

const isPdf = (f: ContactFileModel) =>
  /\.pdf$/i.test(f.originalFileName || f.documentName || "");

/**
 * "Signatures" tab of the client profile's Client portal widget: documents sent
 * for e-signature, their progress, and "Request signature" (Roadmap 1876).
 */
export const SignaturesSection: FC<{
  customerId: string;
  organizationId?: string;
}> = ({ customerId, organizationId }) => {
  const navigate = useNavigate();
  const orgId = getLinkedFirm()?.orgId || "";
  const { data: contact } = useGetContactQuery(
    { customerId, orgId },
    { skip: !!organizationId || !customerId || !orgId },
  );
  const contactFiles = useGetContactFilesQuery(contact?.contactId || "", {
    skip: !!organizationId || !contact?.contactId,
  });
  const orgFiles = useGetOrgContactFilesQuery(organizationId || "", {
    skip: !organizationId,
  });
  const files =
    (organizationId ? orgFiles.data : contactFiles.data)?.contactDocuments ??
    [];
  const pdfs = newestFilesFirst(files.filter(isPdf));

  const { data, isLoading } = useGetClientSignaturesQuery(customerId, {
    skip: !customerId,
  });
  const [remind, remindState] = useRemindSignatureMutation();
  const [cancel, cancelState] = useCancelSignatureMutation();
  const [choosing, setChoosing] = useState(false);
  const [chosen, setChosen] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<SignatureRequestView | null>(
    null,
  );
  const [showAll, setShowAll] = useState(false);

  const list = data ?? [];
  const visible = showAll ? list : list.slice(0, 4);
  const waiting = list.filter((r) => r.status === "sent").length;

  const start = () => {
    const file = pdfs.find((f) => f.id === chosen);
    if (!file) return;
    const name = encodeURIComponent(
      file.originalFileName || file.documentName || "",
    );
    navigate(
      `/signatures/new?customer=${customerId}&doc=${file.id}&name=${name}`,
    );
  };

  const onRemind = async (r: SignatureRequestView) => {
    const result = await remind(r.id);
    if ("error" in result)
      return toast(errorText(result.error), { type: "error" });
    toast("Reminder sent", { type: "success" });
  };

  const onCancel = async () => {
    if (!cancelling) return;
    const result = await cancel(cancelling.id);
    setCancelling(null);
    if ("error" in result)
      return toast(errorText(result.error), { type: "error" });
    toast("Signature request cancelled", { type: "success" });
  };

  const small =
    "h-8 rounded-[8px] border-[1.5px] border-[#DCE2EA] bg-white px-3 text-[12.5px] font-bold text-[#3A4256] hover:bg-[#F7F8FA] disabled:opacity-60";

  return (
    <>
      <div className={"flex flex-col gap-3 text-[#1F2433]"}>
        <div className={"flex items-center gap-3"}>
          <span
            className={
              "min-w-0 flex-1 text-[12.5px] font-medium text-[#6B7185]"
            }>
            {list.length
              ? `${waiting} waiting · ${
                  list.filter((r) => r.status === "completed").length
                } completed`
              : "Send a PDF for e-signature"}
          </span>
          <button
            type={"button"}
            className={`${modalPrimary} !h-8 shrink-0 !rounded-[8px] !px-3 !text-[12.5px]`}
            onClick={() => setChoosing(true)}>
            <LuPenLine size={14} className={"mr-1.5 inline"} />
            Request signature
          </button>
        </div>

        {isLoading && (
          <span className={"text-[13px] font-semibold text-[#6B7185]"}>
            Loading…
          </span>
        )}
        {!isLoading && !list.length && (
          <PortalEmptyState
            testId={"portal-signatures-empty"}
            icon={<LuPenLine size={22} />}
            title={"No signature requests yet"}
            text={
              "Pick one of the client’s PDF files and send it for e-signature."
            }
            actionLabel={"Request signature"}
            onAction={() => setChoosing(true)}
          />
        )}

        <ul className={"m-0 flex list-none flex-col gap-2 p-0"}>
          {visible.map((r) => (
            <li
              key={r.id}
              className={
                "flex flex-col gap-2 rounded-[10px] border border-[#EDEFF3] px-3.5 py-3"
              }>
              <div className={"flex items-start gap-2"}>
                <div className={"flex min-w-0 flex-1 flex-col"}>
                  <span className={"truncate text-[13.5px] font-bold"}>
                    {r.documentName}
                  </span>
                  <span className={"text-[12px] font-semibold text-[#6B7185]"}>
                    Sent {moment(r.sentAt).format("MMM D")} by {r.sentBy}
                    {r.signBy && r.status === "sent"
                      ? ` · sign by ${moment(r.signBy).format("MMM D")}`
                      : ""}
                  </span>
                </div>
                <span
                  className={`shrink-0 rounded-full px-2.5 py-0.5 text-[11.5px] font-bold ${
                    tone[statusTone(r)]
                  }`}>
                  {r.statusText}
                </span>
              </div>
              <div
                className={
                  "flex flex-col gap-0.5 text-[12px] font-semibold text-[#3A4256]"
                }>
                {r.signers.map((s, i) => (
                  <span key={s.id} className={"flex items-center gap-1.5"}>
                    <span
                      className={"h-2 w-2 rounded-full"}
                      style={{ background: s.color }}
                    />
                    {signerLine(s, r.inOrder, r.signers[i - 1]?.name)}
                  </span>
                ))}
              </div>
              <div className={"flex flex-wrap gap-2"}>
                {r.status === "sent" &&
                  r.signers.some(
                    (s) =>
                      s.kind === "firm" &&
                      s.status !== "signed" &&
                      s.status !== "waiting",
                  ) && (
                    <button
                      type={"button"}
                      className={`${small} !border-[#6B4EFF] !text-[#6B4EFF]`}
                      onClick={() => navigate(`/signatures/${r.id}/sign`)}>
                      Sign now
                    </button>
                  )}
                {r.status === "sent" && (
                  <>
                    <button
                      type={"button"}
                      className={small}
                      disabled={remindState.isLoading}
                      onClick={() => onRemind(r)}>
                      Send reminder
                    </button>
                    <button
                      type={"button"}
                      className={small}
                      onClick={() => setCancelling(r)}>
                      Cancel request
                    </button>
                  </>
                )}
                {r.status === "completed" && r.signedDocumentId && (
                  <span className={"text-[12px] font-semibold text-[#147A4B]"}>
                    Signed copy saved to Files and shared with the client
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
        {list.length > 4 && (
          <button
            type={"button"}
            className={"self-start text-[13px] font-bold text-[#6B4EFF]"}
            onClick={() => setShowAll((v) => !v)}>
            {showAll ? "Show less" : `Show all ${list.length}`}
          </button>
        )}
      </div>

      {choosing && (
        <PortalModal
          title={"Request a signature"}
          subtitle={
            "Choose the PDF to send. Upload it to the client’s Files first if it isn’t there."
          }
          onClose={() => setChoosing(false)}
          width={520}
          footer={
            <>
              <button
                type={"button"}
                className={modalCancel}
                onClick={() => setChoosing(false)}>
                Cancel
              </button>
              <button
                type={"button"}
                className={modalPrimary}
                disabled={!chosen}
                onClick={start}>
                Next: place fields
              </button>
            </>
          }>
          {!pdfs.length ? (
            <span className={"text-[13px] font-medium text-[#6B7185]"}>
              This client has no PDF files yet.
            </span>
          ) : (
            <ul
              className={
                "m-0 flex max-h-[340px] list-none flex-col gap-1.5 overflow-y-auto p-0"
              }>
              {pdfs.map((f) => (
                <li key={f.id}>
                  <label
                    className={`flex cursor-pointer items-center gap-3 rounded-[10px] border-[1.5px] px-3 py-2.5 ${
                      chosen === f.id
                        ? "border-[#6B4EFF] bg-[#F7F5FF]"
                        : "border-[#EDEFF3]"
                    }`}>
                    <input
                      type={"radio"}
                      name={"sigdoc"}
                      checked={chosen === f.id}
                      onChange={() => setChosen(f.id)}
                      className={"accent-[#6B4EFF]"}
                    />
                    <span className={"flex min-w-0 flex-1 flex-col"}>
                      <span className={"truncate text-[13.5px] font-bold"}>
                        {f.originalFileName || f.documentName}
                      </span>
                      <span
                        className={"text-[12px] font-semibold text-[#6B7185]"}>
                        Added {moment(f.createdDate).format("MMM D, YYYY")}
                      </span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </PortalModal>
      )}

      {cancelling && (
        <PortalModal
          title={"Cancel this signature request?"}
          subtitle={`${cancelling.documentName} can no longer be signed. Signatures already given are not kept.`}
          onClose={() => setCancelling(null)}
          width={460}
          footer={
            <>
              <button
                type={"button"}
                className={modalCancel}
                onClick={() => setCancelling(null)}>
                Keep it
              </button>
              <button
                type={"button"}
                className={`${modalPrimary} !bg-[#D92D20]`}
                disabled={cancelState.isLoading}
                onClick={onCancel}>
                Cancel request
              </button>
            </>
          }>
          <span className={"text-[13px] font-medium text-[#3A4256]"}>
            The signers are not emailed about it.
          </span>
        </PortalModal>
      )}
    </>
  );
};
